import { useState, useEffect, useCallback } from "react";
import { Star, ThumbsUp, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import AddReviewForm from "./AddReviewForm";

interface Review {
  id: string;
  rating: number;
  title: string | null;
  comment: string | null;
  verified_purchase: boolean;
  helpful_count: number;
  created_at: string;
  user_id: string;
}

interface ReviewStats {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: { [key: number]: number };
}

interface ReviewSystemProps {
  productId: string;
}

const ReviewSystem = ({ productId }: ReviewSystemProps) => {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [stats, setStats] = useState<ReviewStats>({
    averageRating: 0,
    totalReviews: 0,
    ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [showAddReview, setShowAddReview] = useState(false);

  const fetchReviews = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('product_reviews')
        .select('*')
        .eq('product_id', productId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  }, [productId]);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get average rating
      const { data: avgData, error: avgError } = await supabase
        .rpc('get_product_average_rating', { product_uuid: productId });
      
      if (avgError) throw avgError;

      // Get review count
      const { data: countData, error: countError } = await supabase
        .rpc('get_product_review_count', { product_uuid: productId });
      
      if (countError) throw countError;

      // Get rating distribution
      const { data: reviews, error: reviewsError } = await supabase
        .from('product_reviews')
        .select('rating')
        .eq('product_id', productId);

      if (reviewsError) throw reviewsError;

      const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
      reviews?.forEach((review) => {
        distribution[review.rating as keyof typeof distribution]++;
      });

      setStats({
        averageRating: Number(avgData) || 0,
        totalReviews: countData || 0,
        ratingDistribution: distribution
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [fetchReviews, fetchStats]);

  const renderStars = (rating: number, size: "sm" | "md" | "lg" = "md") => {
    const sizeClasses = {
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6"
    };

    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClasses[size]} ${
              star <= rating
                ? "fill-primary text-primary"
                : "text-muted-foreground"
            }`}
          />
        ))}
      </div>
    );
  };

  const getPercentage = (count: number) => {
    return stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0;
  };

  const handleReviewAdded = () => {
    fetchReviews();
    fetchStats();
    setShowAddReview(false);
  };

  if (loading && stats.totalReviews === 0) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-20 bg-muted rounded"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Reviews Summary */}
      <div className="grid md:grid-cols-2 gap-8">
        {/* Overall Rating */}
        <div className="text-center space-y-4">
          <div>
            <div className="text-5xl font-bold text-primary mb-2">
              {stats.averageRating.toFixed(1)}
            </div>
            <div className="flex justify-center mb-2">
              {renderStars(Math.round(stats.averageRating), "lg")}
            </div>
            <p className="text-muted-foreground">
              {stats.totalReviews} avis client{stats.totalReviews !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Rating Distribution */}
        <div className="space-y-3">
          {[5, 4, 3, 2, 1].map((rating) => (
            <div key={rating} className="flex items-center gap-3">
              <span className="text-sm w-8">{rating}</span>
              <Star className="h-4 w-4 fill-primary text-primary" />
              <Progress
                value={getPercentage(stats.ratingDistribution[rating])}
                className="flex-1 h-2"
              />
              <span className="text-sm text-muted-foreground w-12 text-right">
                {stats.ratingDistribution[rating]}
              </span>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Add Review Section */}
      {user && (
        <div className="space-y-4">
          {!showAddReview ? (
            <Button onClick={() => setShowAddReview(true)}>
              Écrire un avis
            </Button>
          ) : (
            <AddReviewForm
              productId={productId}
              onReviewAdded={handleReviewAdded}
              onCancel={() => setShowAddReview(false)}
            />
          )}
        </div>
      )}

      <Separator />

      {/* Reviews List */}
      <div className="space-y-6">
        <h3 className="text-xl font-semibold">
          Avis clients ({stats.totalReviews})
        </h3>
        
        {reviews.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="space-y-4">
                <User className="h-12 w-12 text-muted-foreground mx-auto" />
                <div>
                  <h4 className="font-semibold mb-2">Aucun avis pour le moment</h4>
                  <p className="text-muted-foreground">
                    Soyez le premier à partager votre expérience avec ce produit.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <Card key={review.id}>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {/* Review Header */}
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {renderStars(review.rating)}
                          {review.verified_purchase && (
                            <Badge variant="secondary" className="text-xs">
                              Achat vérifié
                            </Badge>
                          )}
                        </div>
                        {review.title && (
                          <h4 className="font-semibold">{review.title}</h4>
                        )}
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        {new Date(review.created_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>

                    {/* Review Content */}
                    {review.comment && (
                      <p className="text-muted-foreground leading-relaxed">
                        {review.comment}
                      </p>
                    )}

                    {/* Review Actions */}
                    <div className="flex items-center justify-between pt-2">
                      <div className="text-sm text-muted-foreground">
                        Client vérifié
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" className="text-muted-foreground">
                          <ThumbsUp className="h-4 w-4 mr-1" />
                          Utile ({review.helpful_count})
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewSystem;